const UPSELL_UPGRADED_KEY = 'upsell_upgraded_products';

// Match the section IDs that CartDrawer.getSectionsToRender() expects so
// renderContents() gets every section it needs (cart-free-delivery crashes
// on product pages when its section is missing from the response).
function getCartSectionIds() {
  const iconId = document.querySelector('#cart-icon-bubble-short') ? 'cart-icon-bubble-short' : 'cart-icon-bubble';
  const mobileId = document.querySelector('#cart-icon-bubble--mobile-short') ? 'cart-icon-bubble--mobile-short' : 'cart-icon-bubble--mobile';
  return ['cart-drawer', iconId, mobileId, 'cart-free-delivery'];
}

// Stores { productId: appliedUpgradeVariantId } so we know exactly which
// upgrade variant was put in cart. This lets applyUpgradeState() distinguish
// "upgrade still in cart → keep hiding" vs "upgrade removed, original re-added → show again".
function getUpgradedProducts() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(UPSELL_UPGRADED_KEY) || '{}');
    return new Map(Object.entries(raw).map(([k, v]) => [k, String(v)]));
  } catch { return new Map(); }
}

function saveUpgradedProducts(map) {
  sessionStorage.setItem(UPSELL_UPGRADED_KEY, JSON.stringify(Object.fromEntries(map)));
}

function markProductUpgraded(productId, appliedUpgradeVariantId) {
  const map = getUpgradedProducts();
  map.set(String(productId), String(appliedUpgradeVariantId));
  saveUpgradedProducts(map);
}

async function applyUpgradeState() {
  let upgraded = getUpgradedProducts(); // Map: productId → appliedUpgradeVariantId

  if (upgraded.size > 0) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const cartProductIds = new Set(cart.items.map(i => String(i.product_id)));
      const cartVariantIds = new Set(cart.items.map(i => String(i.variant_id)));
      let changed = false;
      for (const [productId, appliedVariantId] of [...upgraded]) {
        // Clear if product gone entirely, OR if the applied upgrade variant is no
        // longer in cart (user removed upgraded product and re-added the original).
        if (!cartProductIds.has(productId) || !cartVariantIds.has(appliedVariantId)) {
          upgraded.delete(productId);
          changed = true;
        }
      }
      if (changed) saveUpgradedProducts(upgraded);
    } catch {}
  }

  document.querySelectorAll('.upsell-cart').forEach(cartEl => {
    const upgradeEl = cartEl.querySelector('upsell-upgrade');
    if (!upgradeEl) return;
    const productId = String(upgradeEl.dataset.productId || '');
    cartEl.classList.toggle('upsell-upgraded', !!(productId && upgraded.has(productId)));
  });
}

class UpsellUpgrade extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('.upsell-upgrade__btn');
    this.btn.addEventListener('click', this.handleUpgrade.bind(this));
    applyUpgradeState();
  }

  async handleUpgrade() {
    const lineKey = this.dataset.lineKey;
    const upgradeVariantId = parseInt(this.dataset.upgradeVariantId);
    const quantity = parseInt(this.dataset.quantity) || 1;
    const productId = String(this.dataset.productId || '');

    this.btn.disabled = true;
    const originalHTML = this.btn.innerHTML;
    this.btn.innerHTML = '<span>...</span>';

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: upgradeVariantId, quantity }],
          sections: getCartSectionIds(),
          sections_url: window.location.pathname
        })
      });

      const data = await addResponse.json();

      if (productId) markProductUpgraded(productId, upgradeVariantId);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      }

      setTimeout(applyUpgradeState, 60);

    } catch (err) {
      console.error('[upsell] Upgrade failed:', err);
      this.btn.disabled = false;
      this.btn.innerHTML = originalHTML;
    }
  }
}

customElements.define('upsell-upgrade', UpsellUpgrade);

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.upsell-product-card__btn');
  if (!btn) return;

  const variantId = parseInt(btn.dataset.variantId);
  if (!variantId) return;

  btn.disabled = true;
  const originalHTML = btn.innerHTML;
  btn.innerHTML = '<span>...</span>';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: getCartSectionIds(),
        sections_url: window.location.pathname
      })
    });

    const data = await response.json();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(data);
    }

    setTimeout(applyUpgradeState, 60);

    // On product page the button stays visible — show brief confirmation
    if (!btn.closest('.upsell-cart')) {
      btn.innerHTML = '<span>Added!</span>';
      setTimeout(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }, 1500);
    }

  } catch (err) {
    console.error('[upsell] Add to cart failed:', err);
    btn.disabled = false;
    btn.innerHTML = originalHTML;
  }
});

document.addEventListener('DOMContentLoaded', applyUpgradeState);

/* ── CAROUSEL ─────────────────────────────────────────────── */
class UpsellCarousel {
  constructor(el) {
    this.el      = el;
    this.inner   = el.querySelector('.upsell-fbt-carousel__inner');
    this.track   = el.querySelector('.upsell-fbt-carousel__track');
    this.dotsEl  = el.querySelector('.upsell-fbt-carousel__dots');
    this.prevBtn = el.querySelector('.upsell-fbt-carousel__arrow--prev');
    this.nextBtn = el.querySelector('.upsell-fbt-carousel__arrow--next');
    this.cards   = [...this.track.querySelectorAll('.upsell-fbt-card')];
    this.pageSize = 2;
    this.pages   = Math.ceil(this.cards.length / this.pageSize);
    this.page    = 0;

    if (this.pages <= 1) {
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      if (this.dotsEl)  this.dotsEl.style.display  = 'none';
      return;
    }

    if (this.dotsEl) {
      for (let i = 0; i < this.pages; i++) {
        const d = document.createElement('button');
        d.className = 'upsell-fbt-carousel__dot' + (i === 0 ? ' active' : '');
        d.setAttribute('aria-label', 'Page ' + (i + 1));
        d.addEventListener('click', () => this.goTo(i));
        this.dotsEl.appendChild(d);
      }
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.page - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.page + 1));

    let t;
    this.track.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => this.syncPage(), 80);
    }, { passive: true });

    this.updateUI();
  }

  goTo(p) {
    p = Math.max(0, Math.min(this.pages - 1, p));
    const card = this.cards[p * this.pageSize];
    if (!card) return;
    const tr = this.track.getBoundingClientRect();
    const cr = card.getBoundingClientRect();
    this.track.scrollTo({ left: this.track.scrollLeft + cr.left - tr.left, behavior: 'smooth' });
    this.page = p;
    this.updateUI();
  }

  syncPage() {
    const p = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (p !== this.page && p >= 0 && p < this.pages) {
      this.page = p;
      this.updateUI();
    }
  }

  updateUI() {
    if (this.dotsEl) {
      this.dotsEl.querySelectorAll('.upsell-fbt-carousel__dot').forEach((d, i) => {
        d.classList.toggle('active', i === this.page);
      });
    }
    if (this.prevBtn) this.prevBtn.disabled = this.page === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.page >= this.pages - 1;
  }
}

function initUpsellCarousels(root) {
  (root || document).querySelectorAll('.upsell-fbt-carousel:not([data-carousel-init])').forEach(el => {
    el.setAttribute('data-carousel-init', '');
    new UpsellCarousel(el);
  });
}

document.addEventListener('DOMContentLoaded', () => initUpsellCarousels());

// Re-init after cart drawer re-renders
const _cartDrawerRoot = document.querySelector('cart-drawer');
if (_cartDrawerRoot) {
  let _mutTimer;
  new MutationObserver(() => {
    clearTimeout(_mutTimer);
    _mutTimer = setTimeout(() => initUpsellCarousels(_cartDrawerRoot), 100);
  }).observe(_cartDrawerRoot, { childList: true, subtree: true });
}
